import * as Three from "three";
import WebGL from "three/examples/jsm/capabilities/WebGL.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Body, G } from "./physics";

function loadWrapper(loader: GLTFLoader, path: string) {
    return new Promise<GLTF>((resolve, reject) => {
        loader.load(
            path,
            (gltf) => {
                resolve(gltf);
            },
            (event) => {
                console.log(`Loading model (${((event.loaded / event.total) * 100).toFixed(2)}%)`);
            },
            (error) => {
                reject(error);
            }
        );
    });
}

const globalScale = 1e-6; // 1 unit = 1 million km

const earthRadius = 6378.1; // in km
const moonRadius = 1738.1; // in km
const sunRadius = 695700; // in km
const earthDistance = 148.72e6; // in km
/**
 * this value gives us the webgl unit to km ratio, it'll be calculated when we load the moon's model
 */
let unitToKm = 0;

async function main() {
    const canvas = document.getElementById("canvas")! as HTMLCanvasElement;
    const simSpeedInput = document.getElementById("simspeed")! as HTMLInputElement; // a slider to control simulation speed
    const accuracyOutput = document.getElementById("accuracy")! as HTMLParagraphElement; // an output to display the accuracy of the simulation
    const loadingText = document.getElementById("loading")! as HTMLParagraphElement; // a text to display while loading

    // check for compatibility
    if (!WebGL.isWebGL2Available()) {
        const warning = WebGL.getWebGL2ErrorMessage();
        document.body.appendChild(warning);
        return;
    }

    const publicRoot = "/programs/two-body-simulation/";

    // load our body model
    const loader = new GLTFLoader();

    const world = new Three.Scene();

    const renderer = new Three.WebGLRenderer({ precision: "highp", canvas });
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = Three.PCFSoftShadowMap;

    const moon = await loadWrapper(loader, publicRoot + "data/moon.glb").then((gltf) => {
        // first scale the model down because Nasa made it too big
        gltf.scene.scale.set(globalScale, globalScale, globalScale);

        // calculate absolute radius of the Moon and derive the unit to km ratio
        const bbox = new Three.Box3().setFromObject(gltf.scene);
        unitToKm = moonRadius / (bbox.max.x - bbox.min.x);

        // position the moon (earthDistance + moonDistance)
        gltf.scene.position.set((earthDistance + 10000) / unitToKm, 0, 0);

        gltf.scene.castShadow = true;
        gltf.scene.receiveShadow = true;

        world.add(gltf.scene);

        return gltf.scene;
    });

    const earth = await loadWrapper(loader, publicRoot + "data/earth.glb").then((gltf) => {
        const earthScale = earthRadius / moonRadius / unitToKm;
        gltf.scene.scale.set(earthScale, earthScale, earthScale);

        gltf.scene.position.set(earthDistance / unitToKm, 0, 0);

        gltf.scene.castShadow = true;
        gltf.scene.receiveShadow = true;

        world.add(gltf.scene);

        return gltf.scene;
    });

    await loadWrapper(loader, publicRoot + "data/sun.glb").then((gltf) => {
        const sunScale = sunRadius / moonRadius / unitToKm;
        gltf.scene.scale.set(sunScale, sunScale, sunScale);

        gltf.scene.position.set(0, 0, 0);

        world.add(gltf.scene);

        return gltf.scene;
    });

    const camera = new Three.PerspectiveCamera(90, canvas.clientWidth / canvas.clientHeight, globalScale, 100);
    camera.position.set(earth.position.x, earth.position.y + 10000 / unitToKm, earth.position.z);
    camera.lookAt(earth.position);

    // add lights
    const sunLight = new Three.PointLight(0xffffff, 1, 0, 0);
    sunLight.position.set(0, 0, 0);
    sunLight.castShadow = true;
    sunLight.shadow.camera.near = globalScale;
    world.add(sunLight);

    const ambient = new Three.AmbientLight(0xffffff, 0.15);
    world.add(ambient);

    // add controls
    const cameraController = new OrbitControls(camera, canvas);
    cameraController.target = earth.position;
    cameraController.zoomSpeed = 1;

    window.addEventListener("resize", () => {
        camera.aspect = canvas.clientWidth / canvas.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
    });

    const clock = new Three.Clock(false);

    const earthBody = new Body(5.972e24, earth.position.clone().multiplyScalar(unitToKm * 1000), new Three.Vector3(0, 0, 0));
    const moonBody = new Body(7.34767309e22, moon.position.clone().multiplyScalar(unitToKm * 1000), new Three.Vector3(0, 0, 5 * 1000));

    // how many seconds per second
    let simulationSpeed = 0;

    function render() {
        const deltaTime = clock.getDelta();

        const distance = earthBody.pos.distanceTo(moonBody.pos);
        if (distance < 2000 / unitToKm) {
            console.log("collision");
            return;
        }

        // update the positions of the bodies
        Body.calcGrav(earthBody, moonBody, deltaTime * simulationSpeed);

        // update the energy
        const energy =
            0.5 * earthBody.mass * earthBody.vel.length() ** 2 +
            0.5 * moonBody.mass * moonBody.vel.length() ** 2 -
            (G * earthBody.mass * moonBody.mass) / earthBody.pos.distanceTo(moonBody.pos);

        // calculate the difference in percentage
        const accuracy = 100 - Math.abs(((energy - initEnergy) / initEnergy) * 100);
        accuracyOutput.innerText = `Accuracy of simulation: ${accuracy.toFixed(2)}%`;

        // update the positions of the models
        earth.position.copy(earthBody.pos.clone().divideScalar(unitToKm * 1000));
        moon.position.copy(moonBody.pos.clone().divideScalar(unitToKm * 1000));

        cameraController.update(deltaTime);

        renderer.render(world, camera);
        requestAnimationFrame(render);
    }

    // init energy
    // calculate the reduced mass system of energy
    const initEnergy =
        0.5 * earthBody.mass * earthBody.vel.length() ** 2 +
        0.5 * moonBody.mass * moonBody.vel.length() ** 2 -
        (G * earthBody.mass * moonBody.mass) / earthBody.pos.distanceTo(moonBody.pos);

    simSpeedInput.addEventListener("input", () => {
        simulationSpeed = simSpeedInput.valueAsNumber;
    });

    loadingText.remove();

    clock.start();
    render();
}

document.addEventListener("DOMContentLoaded", () => {
    main();
});
